import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import TravelCityList from "../../components/TravelCityList";
import TravelConfirmPopup from "../../components/TravelPopup/TravelConfirmPoup";
import TravelTransportList from "../../components/TravelTransportList";

import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { City, CONFIRMATION_DELAY } from "../../constants/const";
import { ItemCategory } from "../../constants/enum/enum";
import { Messages } from "../../constants/messages";
import { TravelTransport } from "../../constants/travel";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  approveGameCashAction,
  getMyProfile,
  getUserTravelInfoAction,
  travelAction,
} from "../../reducers/profile.slice";
import { convertDateTime, toastSuccess } from "../../utils/utils";
import useStyles from "./index.styles";
import config from "../../config/config";
import { getGameBankAllowance } from "../../helper/contractFunctions/MafiaGameBank";

const Travel = () => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { account } = useWallet();
  const currentTime = useCurrentTime();

  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { balance, updateBalance } = useGameBankBalance(
    account,
    signMsg,
    signature
  );
  const { myInventoryItems } = useAppSelector((state) => state.profile);
  const { myProfile, userTravelInfo } = useAppSelector(
    (state) => state.profile
  );

  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [travelType, setTravelType] = useState("Train");
  const [travelCityId, setTravelCityId] = useState(0);

  const renderStatus = () => {
    if (currentTime > userTravelInfo.travelUntil) {
      return "Time to travel!";
    } else {
      return convertDateTime(userTravelInfo.travelUntil - currentTime);
    }
  };

  const handleStartTravel = useCallback(async () => {
    const itemId =
      myInventoryItems.find((item) => {
        if (travelType === "Car/Motorcycle") {
          return (
            item.categoryId === ItemCategory.SHOPITEM &&
            (item.typeId === 8 || item.typeId === 5)
          );
        } else if (travelType === "Airplane") {
          return item.categoryId === ItemCategory.SHOPITEM && item.typeId === 9;
        } else if (travelType === "Train") {
          return true; // Train doesn't require an item
        }
        return false;
      })?.itemId || (travelType === "Train" ? 0 : -1);

    if (itemId === -1) {
      return;
    }

    const cashAmount = TravelTransport[travelType].cost;

    if (cashAmount > balance) {
      toastSuccess(Messages.TRAVEL.START.CASH_INSUFFICIENT);
      return;
    }

    const startTravel = () => {
      dispatchTxAction(
        dispatch,
        travelAction({
          account,
          itemId,
          destinationCity: travelCityId,
          travelType: TravelTransport[travelType].id,
        }),
        () => {
          if (!account || !signMsg || !signature || !myProfile.name) return;
          toastSuccess(Messages.TRAVEL.FINISH.FINISHED_SUCCESS, {
            cityName: City[travelCityId],
          });
          updateBalance(account, signMsg, signature);
          dispatch(getUserTravelInfoAction({ account, signMsg, signature }));
          dispatch(getMyProfile(myProfile.name));
          setOpenConfirmPopup(false);
        },
        CONFIRMATION_DELAY * 2
      );
    };

    const allowance = await getGameBankAllowance(
      account,
      config.profileAddress
    );

    if (allowance < cashAmount) {
      dispatchTxAction(
        dispatch,
        approveGameCashAction({ cashAmount: 10 ** 18, account }),
        () => {
          startTravel();
        },
        CONFIRMATION_DELAY
      );
    } else {
      startTravel();
    }
  }, [
    account,
    balance,
    dispatch,
    myInventoryItems,
    myProfile.name,
    signMsg,
    signature,
    travelCityId,
    travelType,
    updateBalance,
  ]);

  useEffect(() => {
    if (!account || !signMsg || !signature) return;
    dispatch(getUserTravelInfoAction({ account, signMsg, signature }));
  }, [account, dispatch, signMsg, signature]);

  return (
    <Box className={classes.body}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Box className={classes.title}>
              <Typography
                fontFamily={"Philosopher"}
                color={"white"}
                textAlign={"center"}
                fontSize={36}
                fontWeight={700}
              >
                Travel
              </Typography>

              <Box
                component={"img"}
                src={"/assets/imgs/travel/travel.png"}
                alt="Travel Image"
              />
            </Box>

            <Box className={classes.location}>
              <Box
                component={"img"}
                src={"/assets/imgs/travel/placeholder.png"}
                alt="Placeholder"
                sx={{ width: "20px !important", height: 20 }}
              />

              <Typography
                fontFamily={"Philosopher"}
                color={"white"}
                textAlign={"center"}
                fontSize={16}
                fontWeight={900}
              >
                {"Your current location: " +
                  (myProfile.city !== undefined
                    ? City[myProfile.city]
                    : "Unknown")}
              </Typography>
            </Box>
          </Box>

          <Box className={classes.headerRight}>
            <Typography
              fontFamily={"Philosopher"}
              color={"grey"}
              textAlign={"center"}
              fontSize={16}
              fontWeight={700}
            >
              Your next travel:{" "}
            </Typography>
            <Box
              component={"img"}
              src={"/assets/imgs/travel/clockwise.png"}
              alt="Prisoner"
            />

            <Typography
              fontFamily={"Philosopher"}
              color={"white"}
              textAlign={"center"}
              fontSize={16}
              fontWeight={700}
              minWidth={120}
            >
              {renderStatus()}
            </Typography>
          </Box>
        </Box>

        <TravelCityList
          startTravel={(type: string, cityId: number) => {
            setTravelType(type);
            setTravelCityId(cityId);
          }}
          showModal={() => {
            setOpenConfirmPopup(true);
          }}
        />

        <TravelTransportList />
      </Box>

      <TravelConfirmPopup
        travelType={travelType}
        travelCityId={travelCityId}
        handleStartTravel={handleStartTravel}
        openModal={openConfirmPopup}
        setOpenModal={setOpenConfirmPopup}
      />
    </Box>
  );
};

export default Travel;
